import { usePollInterval } from "@src/hooks/usePollInterval";
import { gql } from "graphql-request";
import { useContext, useEffect, useMemo, useState } from "react";
import { useActiveClientsQuery } from "./useActiveClients.generated";
import { PathBuilderContext } from "@src/components/libraryItemDetailPages/module/contexts/PathBuilderContext";
import { CourseBuilderContext } from "@src/components/libraryItemDetailPages/course/contexts/CourseBuilderContext";
import { SkillBuilderContext } from "@builder/skill/contexts/SkillBuilderContext";
import { uniqBy } from "lodash";
import useIsCourseCatalog from "@components/contentLibrary/hooks/useIsCourseCatalog";
import { ClientIdContext } from "@builder/shared/ClientIdContext";
import { ActiveClientFragment } from "../build/CourseBuildContent.generated";
import { useAtomValue } from "jotai";
import { pathVersionIdAtom } from "../../module/atoms";
import { courseVersionIdAtom } from "../atoms";
import { richTextDocumentIdAtom } from "../../resource/atoms";

type UseActiveClients = {
  activeClients: ActiveClientFragment[];
};

const useActiveClients = (): UseActiveClients => {
  const courseBuilderContext = useContext(CourseBuilderContext);
  const pathBuilderContext = useContext(PathBuilderContext);
  const skillBuilderContext = useContext(SkillBuilderContext);
  const [hasActiveClients, setHasActiveClients] = useState(false);
  const pollInterval = usePollInterval(hasActiveClients ? 1000 : 1000 * 10);
  const pathVersionid = useAtomValue(pathVersionIdAtom);
  const courseVersionId = useAtomValue(courseVersionIdAtom);
  const richTextDocumentId = useAtomValue(richTextDocumentIdAtom);
  const { data } = useActiveClientsQuery({
    pollInterval,
    skip: !pathBuilderContext && !courseBuilderContext && !richTextDocumentId,
    variables: {
      input: {
        courseVersionId: courseVersionId,
        pathVersionId: pathVersionid,
        skillVersionId: skillBuilderContext?.skillVersion?.id,
        richTextDocumentId: richTextDocumentId,
      },
    },
  });
  const isCourseCatalog = useIsCourseCatalog();
  const clientIdContext = useContext(ClientIdContext);
  const visibleActiveClients = useMemo(() => {
    if (isCourseCatalog) {
      return undefined;
    }
    return uniqBy(
      data?.ActiveClients.filter(
        (x) => x.clientId !== clientIdContext?.clientId,
      ),
      (c) => c.user.id,
    );
  }, [data?.ActiveClients, clientIdContext?.clientId, isCourseCatalog]);
  useEffect(() => {
    if (visibleActiveClients?.length && !hasActiveClients) {
      setHasActiveClients(true);
    } else if (!visibleActiveClients?.length && hasActiveClients) {
      setHasActiveClients(false);
    }
  }, [visibleActiveClients, hasActiveClients]);
  return useMemo(() => {
    return {
      activeClients: visibleActiveClients || [],
    };
  }, [visibleActiveClients]);
};
export default useActiveClients;
gql`
  query ActiveClients($input: ActiveClientsInput!) {
    ActiveClients: AdminActiveClients(input: $input) {
      ...ActiveClient
    }
  }
`;
